import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DateTimePicker } from "@mui/x-date-pickers";
import MDButton from "components/MDButton";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import orgDataService from "services/orgDataService";
import tripService from "services/tripService";
import { TripHeaderUpdate } from "trips/models";
import * as yup from "yup";
import { useSelector, useDispatch } from "react-redux"; 
import {tripData, getTrip} from "store/tripSlice";

export interface DetailErrors {
  clientId: string;
  controlNumber: string;
  hubId: string;
  pickupLocationId: string;
  pickupDateTime: string;
  isClosed: string;
  shortKey: string;
  dropoffLocation: string;
  distance: string;
  driveTime: string;
  tripType: string;
  estAssistHours: string;
  leadLocomotive: string;
  requesterName: string;
  requesterPhone: string;
  comments: string;
  trainNumber: string;
}

const schema = yup.object({
  clientId: yup.number().required("Master Client is required."),
  controlNumber: yup.string().required("Control Number is required.").max(50),
  pickupDateTimeLocal: yup.string().required("Pickup Date and Time is required."),
  pickupLocationId: yup.number().required("Pickup Point is required."),
  hubId: yup.number().required("Location is required."),
  isClosed: yup.boolean().required(),
  shortKey: yup.string().max(6),
  dropoffLocationId: yup.number().required("Drop Off Point is required"),
  distance: yup.number().max(999, "Distance must be less than or equal to 999."),
  driveTime: yup.number().nullable().max(999, "Drive Time Minutes must be less than or equal to 999."),
  tripTypeId: yup.number().required("Trip Type is required"),
  asap: yup.bool().required("ASAP is required"),
  estAssistHours: yup.number().max(999, "Est. Assist Hours must be less than or equal to 999."),
  leadLocomotive: yup.string().max(50),
  requesterName: yup.string().max(50),
  requesterPhone: yup.string().max(15),
  comments: yup.string(),
  trainNumber: yup.string().max(50),
});

const TripHeaderEdit = ({setIsHeaderEdit, pointsOfService, loadPointsOfService }) => {
  const dispatch = useDispatch();
  const trip = useSelector(tripData);
  const [tripUpdate, setTripUpdate] = useState<TripHeaderUpdate>({
    id: trip.tripNumber,
    controlNumber: trip.controlNumber,
    clientId: trip.clientId,
    hubId: trip.hub?.id,
    isClosed: trip.isClosed,
    pickupLocationId: trip.pickupLocation?.pointOfServiceId,
    pickupDateTimeLocal: trip.pickupDateTimeLocal,
    isApproved: trip.isApproved,
    dropoffLocationId: trip.dropoffLocation?.pointOfServiceId,
    tripTypeId: trip.tripType?.id,
    shortKey: trip.shortKey,
    distance: trip.distance,
    driveTime: trip.driveTime,
    asap: trip.asap,
    estAssistHours: trip.estAssistHours,
    leadLocomotive: trip.leadLocomotive,
    requesterName: trip.requesterName,
    requesterPhone: trip.requesterPhone,
    comments: trip.comments,
    trainNumber: trip.trainNumber,
  });
  const navigate = useNavigate();
  const [errors, setErrors] = useState<DetailErrors>();
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);

  const [tripTypes, setTripTypes] = useState([]);
  const [dropoffLocations, setDropOffLocations] = useState(pointsOfService);
  const [pickupLocations, setPickupLocations] = useState(pointsOfService);

  useEffect(() => {
    orgDataService.getTripTypes().then((results) => setTripTypes(results));
  }, []);

  useEffect(() => {

    if (!trip.dropoffLocation) return;
    // Ensure current location are in the list. They may be missing if the hub was changed. 
    var dropOffLocationFound = dropoffLocations.filter((d) => d.id === trip.dropoffLocation.pointOfServiceId);
    if (dropOffLocationFound.length === 0) {
      let dropoff = [...dropoffLocations];
      dropoff.unshift({id: trip.dropoffLocation.pointOfServiceId, name: trip.dropoffLocation.name});
      setDropOffLocations(dropoff);
    }
     var pickupLocationFound = pickupLocations.filter((d) => d.id === trip.pickupLocation.pointOfServiceId);
    if (pickupLocationFound.length === 0) {
      let pickup = [...pickupLocations];
      pickup.unshift({id: trip.pickupLocation.pointOfServiceId, name: trip.pickupLocation.name});
      setPickupLocations(pickup);
    }
  }, [tripUpdate?.hubId]);

  useEffect(() => {
    orgDataService.getAllClients().then((results) => setClients(results));
  }, []);

  useEffect(() => {
    orgDataService.getHubsByClient(tripUpdate.clientId).then((results) => setLocations(results));
  }, [tripUpdate.clientId]);

  useEffect(() => {
    if (!tripUpdate.hubId) return;
    loadPointsOfService(tripUpdate.clientId, tripUpdate.hubId);
  }, [tripUpdate.hubId]);

  const handleChange = (e) => {
    let name = e.target.name;
    let value = e.target.type === "checkbox" ? e.target.checked : e.target.value;

    tripUpdate[name] = value;
    if (name === "clientId") {
      tripUpdate.hubId = undefined;
      tripUpdate.pickupLocationId = undefined;
    }
    setTripUpdate({ ...tripUpdate });
  };
  function validate() {
    try {
      schema.validateSync(tripUpdate, { abortEarly: false });
      setErrors({
        clientId: "",
        controlNumber: "",
        hubId: "",
        pickupLocationId: "",
        pickupDateTime: "",
        isClosed: "",
        shortKey: "",
        dropoffLocation: "",
        distance: "",
        driveTime: "",
        tripType: "",
        estAssistHours: "",
        leadLocomotive: "",
        requesterName: "",
        requesterPhone: "",
        comments: "",
        trainNumber: "",
      });
      return true;
    } catch (error) {
      let validation = error as yup.ValidationError;
      const newErrors = validation.inner.reduce((acc: any, cur: any) => {
        acc[cur.path] = cur.message;
        return acc;
      }, {});
      setErrors(newErrors);
      return false;
    }
  }

  const saveHeader = () => {
    if (!validate()) return;
    var request = { ...tripUpdate, pickupDateTime: tripUpdate.pickupDateTimeLocal.local().format("MM/DD/YYYY HH:mm") };
    tripService.updateHeader(request).then(() => {
      setIsHeaderEdit(false);
      dispatch(getTrip(trip.tripNumber));
    });
  };
  return (
    <>
      <Grid container spacing={2} p={2}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!!errors?.clientId}>
            <InputLabel id="client-label">Master Client</InputLabel>
            <Select
              name="clientId"
              value={tripUpdate?.clientId || ""}
              label="Master Client"
              labelId="client-label"
              onChange={handleChange}
              fullWidth
              error={!!errors?.clientId}
            >
              {clients &&
                clients.map((s, index) => {
                  return (
                    <MenuItem key={index} value={s.id}>
                      {s.name}
                    </MenuItem>
                  );
                })}
            </Select>
            <FormHelperText error={!!errors?.clientId}>{errors?.clientId}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="controlNumber"
            value={tripUpdate?.controlNumber || ""}
            label="Control Number"
            fullWidth
            inputProps={{ maxLength: 50 }}
            onChange={handleChange}
            error={!!errors?.controlNumber}
          />
          <FormHelperText error={!!errors?.controlNumber}>{errors?.controlNumber}</FormHelperText>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!!errors?.hubId}>
            <InputLabel id="location-label">Location</InputLabel>
            <Select
              name="hubId"
              onChange={handleChange}
              value={tripUpdate?.hubId || ""}
              label="Hub"
              labelId="location-label"
              fullWidth
              error={!!errors?.hubId}
            >
              {locations &&
                locations.map((l, index) => {
                  return (
                    <MenuItem key={index} value={l.id}>
                      {l.name}
                    </MenuItem>
                  );
                })}
            </Select>
            <FormHelperText error={!!errors?.hubId}>{errors?.hubId}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!!errors?.pickupLocationId}>
            <InputLabel id="pickup-point-label">Pickup Point</InputLabel>
            <Select
              name="pickupLocationId"
              value={tripUpdate?.pickupLocationId || ""}
              label="Pickup Point"
              labelId="pickup-point-label"
              onChange={handleChange}
              fullWidth
              error={!!errors?.pickupLocationId}
            >
              {pickupLocations &&
                pickupLocations.map((s, index) => {
                  return (
                    <MenuItem key={index} value={s.id}>
                      {s.name}
                    </MenuItem>
                  );
                })}
            </Select>
            <FormHelperText error={!!errors?.pickupLocationId}>{errors?.pickupLocationId}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!!errors?.pickupDateTime}>
            <DateTimePicker
              name="pickupDateTimeLocal"
              onChange={(e) => setTripUpdate({ ...tripUpdate, pickupDateTimeLocal: e })}
              label="Pickup Date and Time"
              ampm={false}
              value={tripUpdate?.pickupDateTimeLocal || null}
              slotProps={{
                textField: {
                  error: !!errors?.pickupDateTime,
                },
              }}
            />
            <FormHelperText error={!!errors?.pickupDateTime}>{errors?.pickupDateTime}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!!errors?.isClosed}>
            <InputLabel id="trip-status-label">Trip Status</InputLabel>
            <Select
              name="isClosed"
              value={tripUpdate?.isClosed || false}
              label="Trip Status"
              labelId="trip-status-label"
              onChange={handleChange}
              fullWidth
            >
              <MenuItem value="false">Open</MenuItem>
              <MenuItem value="true">Closed</MenuItem>
            </Select>
            <FormHelperText error={!!errors?.isClosed}>{errors?.isClosed}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="shortKey"
            inputProps={{ maxLength: 6 }}
            onChange={handleChange}
            value={tripUpdate?.shortKey || ""}
            label="Short Key"
            fullWidth
            error={!!errors?.shortKey}
          />
          <FormHelperText error={!!errors?.shortKey}>{errors?.shortKey}</FormHelperText>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!!errors?.tripType}>
            <InputLabel>Trip Type</InputLabel>
            <Select
              name="tripTypeId"
              onChange={(e) => setTripUpdate({ ...tripUpdate, tripTypeId: +e.target.value })}
              value={tripUpdate?.tripTypeId || ""}
              label="Trip Type"
            >
              {tripTypes &&
                tripTypes.map((s, index) => {
                  return (
                    <MenuItem key={index} value={s.id}>
                      {s.name}
                    </MenuItem>
                  );
                })}
            </Select>
            <FormHelperText>{errors?.tripType}</FormHelperText>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!!errors?.dropoffLocation}>
            <InputLabel>Drop Off Point</InputLabel>
            <Select
              name="dropoffLocationId"
              onChange={(e) => setTripUpdate({ ...tripUpdate, dropoffLocationId: +e.target.value })}
              value={tripUpdate?.dropoffLocationId || ""}
              label="Drop Off Point"
            >
              {dropoffLocations &&
                dropoffLocations.map((s, index) => {
                  return (
                    <MenuItem key={index} value={s.id}>
                      {s.name}
                    </MenuItem>
                  );
                })}
            </Select>
            <FormHelperText>{errors?.dropoffLocation}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Call Date and Time"
            value={
              trip?.callDateTime && trip.callDateTime.isValid?.()
                ? trip.callDateTime.format("MM/DD/YYYY HH:mm")
                : ""
            }
            InputProps={{ readOnly: true }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={<Checkbox name="asap" onChange={handleChange} checked={!!tripUpdate?.asap} />}
            label="ASAP"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="distance"
            onChange={handleChange}
            value={tripUpdate?.distance || 0}
            label="Distance (Hub to Pickup)"
            fullWidth
            type="number"
            error={!!errors?.distance}
            inputProps={{ maxLength: 3 }}
          />
          <FormHelperText error={!!errors?.distance}>{errors?.distance}</FormHelperText>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!!errors?.driveTime}>
            <TextField
              name="driveTime"
              onChange={handleChange}
              value={tripUpdate?.driveTime || 0}
              label="Drive Time Hours"
              type="number"
              fullWidth
              inputProps={{ maxLength: 3 }}
            />
            <FormHelperText error={!!errors?.driveTime}>{errors?.driveTime}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            name="estAssistHours"
            onChange={handleChange}
            value={tripUpdate?.estAssistHours || 0}
            label="Estimated Assist Hours"
            type="number"
            fullWidth
            inputProps={{ maxLength: 3 }}
            error={!!errors?.estAssistHours}
          />
          <FormHelperText error={!!errors?.estAssistHours}>{errors?.estAssistHours}</FormHelperText>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            name="leadLocomotive"
            onChange={handleChange}
            value={tripUpdate?.leadLocomotive || ""}
            label="Lead Locomotive"
            fullWidth
            inputProps={{ maxLength: 50 }}
            error={!!errors?.leadLocomotive}
          />
          <FormHelperText error={!!errors?.leadLocomotive}>{errors?.leadLocomotive}</FormHelperText>
        </Grid>
      
        <Grid item xs={12} md={6}>
          <TextField
            name="requesterName"
            onChange={handleChange}
            value={tripUpdate?.requesterName || ""}
            label="Requester Name"
            fullWidth
            inputProps={{ maxLength: 50 }}
            error={!!errors?.requesterName}
          />
          <FormHelperText error={!!errors?.requesterName}>{errors?.requesterName}</FormHelperText>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            name="requesterPhone"
            onChange={handleChange}
            value={tripUpdate?.requesterPhone || ""}
            label="Requester Phone"
            fullWidth
            inputProps={{ maxLength: 15 }}
            error={!!errors?.requesterPhone}
          />
          <FormHelperText error={!!errors?.requesterPhone}>{errors?.requesterPhone}</FormHelperText>
        </Grid>
        <Grid item xs={12}>
          <TextField
            name="comments"
            onChange={handleChange}
            value={tripUpdate?.comments || ""}
            label="Comments"
            fullWidth
            multiline
            rows={4}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            name="trainNumber"
            onChange={handleChange}
            value={tripUpdate?.trainNumber || ""}
            label="Train Number"
            fullWidth
            inputProps={{ maxLength: 50 }}
            error={!!errors?.trainNumber}
          />
          <FormHelperText error={!!errors?.trainNumber}>{errors?.trainNumber}</FormHelperText>
        </Grid>
        <Grid item xs={12} sx={{ display: "flex", flexDirection: "row-reverse" }}>
          <MDButton type="button" onClick={saveHeader} variant="contained" color="primary">
            Save
          </MDButton>
          <MDButton
            sx={{ mr: 2 }}
            type="button"
            variant="contained"
            color="secondary"
            onClick={() => setIsHeaderEdit(false)}
          >
            Cancel
          </MDButton>
        </Grid>
      </Grid>
    </>
  );
};
export default TripHeaderEdit;
